import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { IvrCallDetails } from 'src/app/models/IvrCallDetails';
import { LoggedInUserDetails } from 'src/app/models/LoggedInUserDetails';
import { CoreService } from 'src/app/services/core/core.service';
import { StorageService } from 'src/app/services/storage/storage.service';

@Injectable({
  providedIn: 'root'
})
export class HomeService {
  /* Data from Local Storage (LS) */
  loggedInRoleLS: string = this.storageService.getItem('role');
  masterDataLS: any = this.storageService.getItem('master');
  loggedInUserDataLS: LoggedInUserDetails = this.storageService.getItem('logged_in_user_details');
  coachesDataLS: any = this.storageService.getItem('coaches');
  currentClientLS: any = this.storageService.getItem('current_client');

  constructor(private coreService: CoreService, private storageService: StorageService) {};

  getMasters(payload: any): Observable<any> {
    return this.coreService.callPostApi('common/masters', payload);
  }

  getReferredDoctors(payload: any): Observable<any> {
    return this.coreService.callPostApi('common/referred_doctors', payload);
  }

  getCampaigns(payload: any): Observable<any> {
    return this.coreService.callPostApi('common/campaigns', payload);
  }

  getUserDetailsData(payload: any): Observable<any> {
    return this.coreService.callPostApi('user/get_details', payload, true);
  }

  getCoaches(payload: any): Observable<any> {
    return this.coreService.callPostApi('common/coaches', payload);
  }

  logout(payload: any): Observable<any> {
    return this.coreService.callPostApi('user/social_logout', payload);
  }

  createDeeplink(payload: any): Observable<any> {
    return this.coreService.callPostApi('common/create_deeplink', payload);
  }

  unreadNotifications(payload: any): Observable<any> {
    return this.coreService.callPostApi('common/unread_count_notifications', payload, true);
  }

  unreadChatCount(payload: any): Observable<any> {
    return this.coreService.callPostApi('common/unread_chat_count', payload, true);
  }

  /* Observables */
  private _masterDetails: BehaviorSubject<{}> = new BehaviorSubject(this.masterDataLS || {});
  public get masterDetails(): Observable<{}> {
    return this._masterDetails.asObservable();
  }
  setAllMasterDetails(data: any): void {
    this._masterDetails.next(data);
  }

  private _userDetails: BehaviorSubject<LoggedInUserDetails | {}> = new BehaviorSubject(this.loggedInUserDataLS || {});
  public get userDetails(): Observable<LoggedInUserDetails | {}> {
    return this._userDetails.asObservable();
  }
  setUserDetails(data: LoggedInUserDetails): void {
    this._userDetails.next(data);
  }
  getUserDetails(): LoggedInUserDetails | {} {
    return this._userDetails.getValue();
  }

  private _currentRole: BehaviorSubject<string | null> = new BehaviorSubject(this.loggedInRoleLS || null);
  public get currentRole(): Observable<string | null> {
    return this._currentRole.asObservable();
  }
  setCurrentRole(data: string | null): void {
    this._currentRole.next(data);
  }
  getCurrentRole(): string | null {
    return this._currentRole.getValue();
  }

  private _unreadChatCounts: BehaviorSubject<any> = new BehaviorSubject('');
  public get unreadChatCounts(): Observable<any> {
    return this._unreadChatCounts.asObservable();
  }
  setUnreadChatCounts(data: any): void {
    this._unreadChatCounts.next(data);
  }

  private _unreadNotificationCounts: BehaviorSubject<any> = new BehaviorSubject('');
  public get unreadNotificationCounts(): Observable<any> {
    return this._unreadNotificationCounts.asObservable();
  }
  setUnreadNotificationCount(data: any): void {
    this._unreadNotificationCounts.next(data);
  }

  private _coaches: BehaviorSubject<{}> = new BehaviorSubject(this.coachesDataLS || {});

  public get coaches(): Observable<{}> {
    return this._coaches.asObservable();
  }
  setCoaches(data: any): void {
    this._coaches.next(data);
  }

  private ivr: BehaviorSubject<IvrCallDetails | null> = new BehaviorSubject<IvrCallDetails | null>(null);
  ivrCall: Observable<IvrCallDetails | null> = this.ivr.asObservable();

  triggerIvr(ivrCallDetails: IvrCallDetails | null): void {
    this.ivr.next(ivrCallDetails);
  }

  // Mention location / page from where IVR is closing
  private ivrClose: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  ivrCallDestroy: Observable<string | null> = this.ivrClose.asObservable();

  onCloseIvrCall(location: string): void {
    this.ivrClose.next(location);
  }

  private autoCallTaskDialed = new Subject<number>();
  autoCallTaskDialed$ = this.autoCallTaskDialed.asObservable();

  notifyAutoCallTaskDialed(taskId: number): void {
    this.autoCallTaskDialed.next(taskId);
  }

  private _currentClient: BehaviorSubject<{}> = new BehaviorSubject(this.currentClientLS ?? null);
  public get currentClient(): Observable<{}> {
    return this._currentClient.asObservable();
  }
  setCurrentClient(data: any): void {
    this._currentClient.next(data);
  }
  getCurrentClient(): any {
    return this._currentClient.getValue();
  }
  openJourneyNotes$ = new Subject<{ call: any; groupedCalls: any[] }>();
}
